/* eslint-disable max-lines */
import { useAddLog } from '@/modules/logging/compositions'
import { useCurrentTeam } from '@/modules/teams/compositions'
import { ref, computed, onUnmounted } from '@vue/composition-api'
import { CAST_TYPE_LABEL, CREW_LOCATION_LABELS, CastDetailCsv, CrewLocation } from '../types'
import { useRouter } from '@/modules/app/compositions'
import useCastTemplates from '@/compositions/castTemplates'
import CsvExporter from '@/modules/common/classes/CsvExporter'
import { Result } from '@/modules/errorHandling/results'
import { KeyDict, ObjectKeys, PartialMap } from '@/types'
import { COUNTRIES, OLYMPIC_SPORTS } from '@/modules/kisweui/data'
import { OLYMPIC_VENUES } from '@/modules/kisweui/data/venues'
import useCastProperties from '@/modules/castconfiguration/compositions/castProperties'
import fb from '@/firebase'
import firebase from 'firebase/app'
import { CollectionNames } from '@/modules/database/utils'
import { LinkPointer, isEventLinkPointer } from '@/classes/LinkPointer/LinkPointer'
import EventLinkPointer from '@/classes/LinkPointer/LinkPointerTypes/EventLinkPointer'
import { Cast } from '@/modules/casts/classes'

export const CAST_CSV_VIEW_ORDERED: ObjectKeys<CastDetailCsv> = [
  'name', 'url', 'type', 'template',
  'startDate', 'startTime', 'endDate', 'endTime',
  'bitRate', 'resolution', 'frameRate', 'crewLocation', 'region',
  'athleteFName', 'athleteLName', 'discipline', 'country', 'venue', 'china', 'backup', 'backupRegion', 'guestLink'
]
export const CSV_HEADER_MAP: PartialMap<CastDetailCsv, string> = {
  'name': 'Name',
  'url': 'URL',
  'type': 'Cast Type',
  'template': 'Template',
  'startDate': 'Start Date',
  'startTime': 'Start Time',
  'endDate': 'End Date',
  'endTime': 'End Time',
  'bitRate': 'Bit Rate',
  'resolution': 'Resolution',
  'frameRate': 'Frame Rate',
  'crewLocation': 'Crew Location',
  'region': 'Region',
  'athleteFName': 'First Name',
  'athleteLName': 'Last Name',
  'discipline': 'Discipline',
  'country': 'Country',
  'venue': 'Venue',
  'china': 'China support',
  'backup': 'Backup support',
  'backupRegion': 'Backup Region',
  'guestLink': 'Guest Link'
}

const CAST_BATCH_SIZE = 100 // To limit number of casts returned to not cause query to timeout
const POINTER_BATCH_SIZE = 10 // firebase limit for .where('..', 'in', [..])

interface EventLinkPointerWithId {
  pointerId: string,
  pointer: EventLinkPointer
}

interface CastPage {
  casts: Cast[],
  snapLen: number,
  lastDocSnapshot: firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>
}

const downloadDetailsToCsv = (castDetails: CastDetailCsv[], fileName: string) => {
  CsvExporter.download(CsvExporter.convert(castDetails, CAST_CSV_VIEW_ORDERED, CSV_HEADER_MAP), fileName)
}

const getEntityName = (dict: KeyDict<any>, key: string|null|undefined): string => dict[key ?? '']?.name ?? ''

// eslint-disable-next-line max-lines-per-function
const useCastDetailsToCsv = () => {

  const loadingProgress = ref(0)
  const isLoadingDetails = computed(() => loadingProgress.value > 0)
  const { teamId, currentTeam } = useCurrentTeam()
  const { addLog } = useAddLog()
  const { getUrl } = useRouter()
  const { templates } = useCastTemplates()
  const { castProperties } = useCastProperties()
  let timeout: number|null = null

  const getGuestLink = (pointerWithId: EventLinkPointerWithId|undefined, isFixedRegion: boolean): string => {
    if (!pointerWithId) return ''
    const dnsOverrides = currentTeam.value?.dnsOverrides
    if (!dnsOverrides) return ''
    return pointerWithId.pointer.getPublicPointerUrl(pointerWithId.pointerId, dnsOverrides, isFixedRegion)
  }

  const getNextCastPage =
    async (lastDocSnapshot: firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>|null,
      hasCompletedCasts: boolean, batchSize: number): Promise<CastPage> =>
  {
    const endDateComparator = hasCompletedCasts ? '<=' : '>'
    const snapshot = await fb.db.collection(CollectionNames.CASTS)
      .where('team_id', '==', teamId.value)
      .where('end_date', endDateComparator, new Date())
      .where('deleted', '==', false)
      .orderBy('end_date')
      .startAfter(lastDocSnapshot)
      .limit(batchSize)
      .get()
    const casts: Cast[] = []
    for (const doc of snapshot.docs) {
      const result = Cast.load(doc.data())
      if (result.isSuccess) casts.push(result.value)
      else result.logIfError(`Error converting cast document with id: ${doc.id}`)
    }
    return { casts, snapLen: snapshot.size, lastDocSnapshot: snapshot.docs[snapshot.size - 1] }
  }

  const getPointersForCastIds = async (castIds: string[]): Promise<KeyDict<EventLinkPointerWithId>> => {
    const castIdBatches: string[][] = []
    const linkPointers: KeyDict<EventLinkPointerWithId> = {}
    for (let i=0; i < castIds.length; i = i+POINTER_BATCH_SIZE) {
      castIdBatches.push(castIds.slice(i, i+POINTER_BATCH_SIZE))
    }
    const promises = castIdBatches.map((castIdBatch) =>
      fb.db.collection(CollectionNames.POINTERS)
        .where('team_id', '==', teamId.value)
        .where('eventId', 'in', castIdBatch)
        .get()
    )
    const snapshots = await Promise.all(promises)
    for (const snapshot of snapshots) {
      for (const pointerDoc of snapshot.docs) {
        const result = LinkPointer.load(pointerDoc.data())
        if (result.isSuccess && isEventLinkPointer(result.value)) {
          linkPointers[result.value.eventId] = { pointerId: pointerDoc.id, pointer: result.value }
        } else {
          result.logIfError(`Failed to create LinkPointer object for pointer: ${pointerDoc.id}`)
        }
      }
    }
    return linkPointers
  }

  // eslint-disable-next-line complexity
  const transformCastToCsv = (casts: Cast[], linkPointers: KeyDict<EventLinkPointerWithId>): CastDetailCsv[] => {
    const transformedRecords: CastDetailCsv[] = []
    for (const cast of casts) {
      const castCsvRecord: CastDetailCsv = {
        name: cast.name,
        url: window.location.origin + getUrl('liveEvent', { id: cast.id }),
        type: CAST_TYPE_LABEL[cast.castType],
        template: cast.templateId ? templates.value[cast.templateId]?.name : '',
        startDate: cast.start_date.toLocaleDateString(),
        startTime: cast.start_date.toLocaleTimeString(),
        endDate: cast.end_date.toLocaleDateString(),
        endTime: cast.end_date.toLocaleTimeString(),
        resolution: cast.quality.slice(0,5),
        bitRate: Number(cast.quality.slice(5)),
        frameRate: cast.framerate,
        crewLocation: cast.isCrossContinent ?
          CREW_LOCATION_LABELS[CrewLocation.Global] :
          CREW_LOCATION_LABELS[CrewLocation.Continent],
        region: castProperties.value[cast.region ?? '']?.name ?? '',
        athleteFName: cast.scenesSettings.athlete.firstName ?? '',
        athleteLName: cast.scenesSettings.athlete.lastName ?? '',
        discipline: getEntityName(OLYMPIC_SPORTS, cast.scenesSettings.athlete.discipline),
        country: getEntityName(COUNTRIES, cast.scenesSettings.athlete.country),
        venue: getEntityName(OLYMPIC_VENUES, cast.scenesSettings.athlete.venue),
        china: cast.regionalInfo.fixed ? 'On' : 'Off',
        backup: !!cast.regionBackup ? 'On' : 'Off',
        backupRegion: castProperties.value[cast.regionBackup ?? '']?.name ?? '',
        guestLink: linkPointers[cast.id] ? getGuestLink(linkPointers[cast.id], cast.regionalInfo.fixed) : '' ?? ''
      }
      transformedRecords.push(castCsvRecord)
    }
    return transformedRecords
  }

  const loadCastDetails = async (hasCompletedCasts: boolean, hasGuestLinks: boolean = false):
    Promise<Result<CastDetailCsv[], 'invalid_params'|'database'>> =>
  {
    if (!teamId.value) return Result.fail('invalid_params', 'Failed fetching team cast details. Missing teamId')

    const castDetailList: CastDetailCsv[] = []
    loadingProgress.value = 0.001
    let castPage: CastPage|null = null
    do {
      castPage = await getNextCastPage(castPage ? castPage.lastDocSnapshot : null, hasCompletedCasts, CAST_BATCH_SIZE)
      let linkPointers: KeyDict<EventLinkPointerWithId> = {}
      if (hasGuestLinks) {
        const castIds = castPage.casts.map((cast) => cast.id)
        linkPointers = await getPointersForCastIds(castIds)
      }
      const castDetailRecords = transformCastToCsv(castPage.casts, linkPointers)
      castDetailList.push(...castDetailRecords)
      loadingProgress.value = loadingProgress.value + castPage.casts.length
    } while (castPage.snapLen === CAST_BATCH_SIZE)

    addLog(`Successfully fetched ${castDetailList.length} cast detail records for team: ${currentTeam.value?.name}`,
      'info').catch((error) => console.error('Error logging loading of cast details', error))
    timeout = window.setTimeout(() => {
      loadingProgress.value = 0
      timeout = null
    }, 1000)
    return Result.success(castDetailList)
  }

  onUnmounted(() => {
    if (timeout) {
      window.clearTimeout(timeout)
      timeout = null
    }
  })

  return {
    isLoadingDetails,
    loadCastDetails,
    loadingProgress,
    downloadDetailsToCsv
  }
}

export default useCastDetailsToCsv
